import { useMutation, useQuery } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { Link, useNavigate, useParams } from "react-router";

import { useApiClient } from "@/api/client-context";
import { caseActionPath, caseDetailPath, ROUTES } from "@/app/routes";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { parseMessageStart } from "@/domain/process-model/message-start";
import { ProcessOverview } from "@/features/case-diagram/process-overview";
import { ProcessDescription } from "@/features/process-design/process-description";
import {
  processModelFileQueryOptions,
  processModelQueryOptions,
} from "@/features/process-design/use-process-model";
import { notifySuccess } from "@/lib/notify";

import {
  describeStartError,
  type StartErrorView,
} from "./describe-start-error";
import { ScopedStartForm } from "./scoped-start-form";
import { startApplication } from "./use-start-application";

/**
 * 案件種別の詳細（作成の前に内容を確認する画面）。
 *
 * カタログでクリックした時点では**案件を作らない**。以前は選んだ瞬間に instance を
 * 作っていたため、流れを確認するだけで下書きが増えていた。
 *
 * 起票の経路はモデルによって違うので、この画面で吸収する。
 * - 通常のモデル: 「この種別で作成」で instance を作り、起案タスクの入力画面へ
 * - メッセージ起動のモデル: 起案タスクを持たないので起票フォームを出し、提出で作成
 */
export function ApplicationTypePage() {
  const { t } = useTranslation(["common", "domain"]);
  const navigate = useNavigate();
  const client = useApiClient();
  const params = useParams();

  const processModelId = (params.modifiedModelId ?? "").replace(/:/g, "/");

  const model = useQuery({
    ...processModelQueryOptions(client, processModelId),
    enabled: processModelId !== "",
  });

  // メッセージ起票かどうかは BPMN で決まる（フロントにモデルの一覧を持たない）。
  // 図の表示と同じクエリなので追加の取得は発生しない
  const primaryFileName = model.data?.primaryFileName ?? null;
  const diagram = useQuery({
    ...processModelFileQueryOptions(
      client,
      processModelId,
      primaryFileName ?? "",
    ),
    enabled: processModelId !== "" && primaryFileName !== null,
  });
  const scoped =
    diagram.data === undefined
      ? null
      : parseMessageStart(diagram.data.contents);

  const start = useMutation({
    mutationFn: () => startApplication(client, { processModelId }),
    onSuccess: (result) => {
      // 起票の手応えは経路で変わらないようにする（部署スコープ版も出す）。
      // ただしここはまだ提出していないので、タスクが開いたなら「下書き」と言う
      notifySuccess(
        t(
          result.taskGuid === null
            ? "common:toast.applicationStarted"
            : "common:toast.draftCreated",
        ),
      );

      // タスクが開かなかった場合（自動進行など）は一覧で状態を見せる。
      // 開いた場合は案件詳細の操作パネルへ（提出の画面は詳細に一本化している）
      void navigate(
        result.taskGuid === null
          ? ROUTES.myApplications
          : caseActionPath(result.caseId, processModelId),
      );
    },
  });

  return (
    <section className="flex flex-col gap-5">
      <header className="flex flex-col gap-1">
        <Link
          to={ROUTES.newApplication}
          // BackLink と同じ理由で幅を文字に収める（親が flex-col）
          className="text-muted-foreground hover:text-foreground w-fit text-sm"
        >
          {t("common:newApplication.typeDetail.back")}
        </Link>
        <h1 className="text-xl font-semibold">
          {model.data?.displayName ?? t("common:newApplication.title")}
        </h1>
        {model.data?.description ? (
          <p className="text-muted-foreground text-sm">
            {model.data.description}
          </p>
        ) : null}
      </header>

      {model.isPending ? <Skeleton className="h-24 w-full" /> : null}

      {model.isError ? (
        <p role="alert" className="text-destructive text-sm">
          {t("common:newApplication.typeDetail.unknown")}
        </p>
      ) : null}

      {model.data ? (
        <>
          {/* 作成前に流れを見せる。図が主役なので画面幅を使う */}
          <ProcessOverview processModelId={processModelId} />

          {/* 何を始めるのか決める場面なので、説明も既定で開いて読ませる */}
          <ProcessDescription processModelId={processModelId} defaultOpen />

          {scoped === null ? (
            <PlainStart
              processModelId={processModelId}
              isCreating={start.isPending}
              error={start.error}
              onCreate={() => start.mutate()}
            />
          ) : (
            <ScopedStartForm
              scoped={scoped}
              processModelId={processModelId}
              onStarted={() => void navigate(ROUTES.myApplications)}
            />
          )}
        </>
      ) : null}
    </section>
  );
}

/** 通常のモデル: ここで初めて案件を作る。 */
function PlainStart({
  processModelId,
  isCreating,
  error,
  onCreate,
}: {
  readonly processModelId: string;
  readonly isCreating: boolean;
  readonly error: Error | null;
  readonly onCreate: () => void;
}) {
  const { t } = useTranslation("common");
  const view = error === null ? null : describeStartError(error);
  // 案件ができた後に押すと 2 件目ができるので、作れた案件へ誘導して押させない
  const createdCaseId = view?.caseId ?? null;

  return (
    <div className="flex flex-col gap-2">
      <p className="text-muted-foreground text-sm">
        {t("newApplication.typeDetail.hint")}
      </p>
      {view === null ? null : (
        <StartError view={view} processModelId={processModelId} />
      )}
      <div>
        <Button
          type="button"
          disabled={isCreating || createdCaseId !== null}
          onClick={onCreate}
        >
          {t("newApplication.typeDetail.create")}
        </Button>
      </div>
    </div>
  );
}

/**
 * 起票の失敗。backend が返した理由はそのまま見せる（モデルを作った人が自分で
 * BPMN を直せるように。#43）。
 */
function StartError({
  view,
  processModelId,
}: {
  readonly view: StartErrorView;
  readonly processModelId: string;
}) {
  const { t } = useTranslation("common");

  return (
    <div role="alert" className="text-destructive flex flex-col gap-1 text-sm">
      {/* 案件が作れた後の失敗では「作成できませんでした」と言わない */}
      {view.caseId === null ? (
        <p className="m-0">{t("newApplication.typeDetail.createFailed")}</p>
      ) : null}
      {view.reason === "forbidden" ? (
        <p className="m-0">{t("newApplication.typeDetail.createForbidden")}</p>
      ) : null}
      {view.reason === "model-invalid" ? (
        <p className="m-0">
          {t("newApplication.typeDetail.createModelInvalid")}
        </p>
      ) : null}
      {view.caseId === null ? null : (
        <p className="m-0">
          {/* 実行で失敗したら案件はエラーで止まる。タスクの取得で失敗しただけなら
              案件は起案タスクで待っている */}
          {t(
            view.stage === "tasks"
              ? "newApplication.typeDetail.createdCaseOpen"
              : "newApplication.typeDetail.createdCaseStopped",
            { id: String(view.caseId) },
          )}{" "}
          <Link
            to={caseDetailPath(view.caseId, processModelId)}
            className="underline underline-offset-2"
          >
            {t("newApplication.typeDetail.openCase")}
          </Link>
        </p>
      )}
      {view.detail === null ? null : (
        <div className="border-border bg-muted text-foreground flex flex-col gap-1 rounded-md border p-2 text-xs">
          <p className="text-muted-foreground m-0">
            {t("newApplication.typeDetail.createDetail")}
          </p>
          {/* 長いエラーメッセージでも画面を崩さない。スクロールする領域なので
              キーボードでも読めるようにフォーカスを受け取る */}
          <pre
            tabIndex={0}
            className="m-0 max-h-40 overflow-auto font-mono break-words whitespace-pre-wrap"
          >
            {view.detail}
          </pre>
        </div>
      )}
      {view.elementId === null ? null : (
        <p className="text-muted-foreground m-0 text-xs">
          {t("newApplication.typeDetail.createErrorElement", {
            id: view.elementId,
          })}
        </p>
      )}
      {view.stage === null ? null : (
        <p className="text-muted-foreground m-0 text-xs">
          {view.errorCode === null
            ? t("newApplication.typeDetail.createErrorStage", {
                stage: t(`newApplication.typeDetail.stage.${view.stage}`),
              })
            : t("newApplication.typeDetail.createErrorMeta", {
                stage: t(`newApplication.typeDetail.stage.${view.stage}`),
                code: view.errorCode,
              })}
        </p>
      )}
      {view.retryable ? (
        <p className="m-0">{t("newApplication.typeDetail.createRetry")}</p>
      ) : null}
    </div>
  );
}
